import { useToggle } from '@dealstack/frontend/shared/components/ExpandablePanel/useToggle';
import {
	CreateVestingTermLeaverProvisionDto,
	UpdateVestingTermLeaverProvisionDto,
	VestingTermLeaverProvisionRes,
	VestingTermLeaverProvisionType,
	VestingTermPurchaseTermsType,
} from '@dealstack/shared/lib/generated';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
// eslint-disable-next-line no-restricted-imports
import { TFunction } from 'i18next';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';

const getLeaverProvisionFormShape = (t: TFunction) =>
	z.object({
		type: z.nativeEnum(VestingTermLeaverProvisionType),
		vestedSharesPercentage: z
			.number({
				invalid_type_error: t('vesting_terms.error.leaver_provision.percentage.number'),
				required_error: t('vesting_terms.error.leaver_provision.percentage.required'),
			})
			.nonnegative(t('vesting_terms.error.leaver_provision.percentage.nonnegative'))
			.max(100, t('vesting_terms.error.leaver_provision.percentage.max')),
		unvestedSharesPercentage: z
			.number({
				invalid_type_error: t('vesting_terms.error.leaver_provision.percentage.number'),
				required_error: t('vesting_terms.error.leaver_provision.percentage.required'),
			})
			.nonnegative(t('vesting_terms.error.leaver_provision.percentage.nonnegative'))
			.max(100, t('vesting_terms.error.leaver_provision.percentage.max')),
		vestedPurchaseTerms: z.nativeEnum(VestingTermPurchaseTermsType),
		unvestedPurchaseTerms: z.nativeEnum(VestingTermPurchaseTermsType),
	});

export const useLeaverProvisionController = (
	vestingTermId: string,
	setLeaverProvisionCreatorOpen: (bool: boolean) => void,
	onCreateVestingTermLeaverProvision: (
		vestingTermLeaverProvision: CreateVestingTermLeaverProvisionDto
	) => void,
	onUpdateVestingTermLeaverProvision: (
		straightLineTermId: string,
		formValues: UpdateVestingTermLeaverProvisionDto
	) => void,
	leaverProvision?: VestingTermLeaverProvisionRes
) => {
	const tCommonUi = useTranslationCommonUi();

	const { isExpanded, toggleExpanded } = useToggle();

	const {
		control,
		handleSubmit,
		setValue,
		formState: { errors },
	} = useForm({
		mode: 'all',
		defaultValues: {
			type: leaverProvision?.type ?? VestingTermLeaverProvisionType.GOOD_LEAVER,
			vestedSharesPercentage: leaverProvision?.vestedSharesPercentage ?? 0,
			vestedPurchaseTerms:
				leaverProvision?.vestedPurchaseTerms ?? VestingTermPurchaseTermsType.PURCHASE_PRICE,
			unvestedPurchaseTerms:
				leaverProvision?.unvestedPurchaseTerms ??
				VestingTermPurchaseTermsType.PURCHASE_PRICE,
			unvestedSharesPercentage: leaverProvision?.unvestedSharesPercentage ?? 0,
		},
		resolver: zodResolver(getLeaverProvisionFormShape(tCommonUi)),
	});

	const onSubmit = (data: UpdateVestingTermLeaverProvisionDto) => {
		if (leaverProvision) {
			onUpdateVestingTermLeaverProvision(leaverProvision.id, data);

			if (isExpanded) {
				toggleExpanded();
			}
		} else {
			onCreateVestingTermLeaverProvision({ ...data, vestingTermId });
			setLeaverProvisionCreatorOpen(false);
		}
	};

	return {
		toggleExpanded,
		isExpanded,
		control,
		handleSubmit,
		onSubmit,
		setValue,
		errors,
	};
};
